"use client";

import { useRouter } from "next/navigation";
import { useCallback, useRef, useState } from "react";
import { apiFetch } from "@/lib/api";

const PLACEHOLDER =
  "Describe the assessment goal, scope, and constraints—for example: “Q1 cloud spend is 18% over plan in the data platform org; we need a prioritized savings path that doesn’t block releases.” You can also note what files you’ll attach.";

interface LandingAssessmentComposerProps {
  message?: string;
  onMessageChange?: (msg: string) => void;
}

export function LandingAssessmentComposer({ message = "", onMessageChange }: LandingAssessmentComposerProps) {
  const router = useRouter();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);

  const onFilesPicked = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const list = e.target.files;
      if (!list?.length) return;
      setFiles(Array.from(list));
    },
    [],
  );

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!message.trim() && files.length === 0) return;
      
      setIsSubmitting(true);
      try {
        const response = await apiFetch("/chat/start", {
          method: "POST",
        });
        const data = await response.json();
        
        if (data.sessionId) {
          // Send the message and files immediately for a seamless transition
          const formData = new FormData();
          formData.append("sessionId", data.sessionId);
          formData.append("message", message.trim() || "Let's start the assessment.");
          if (files.length > 0) {
            files.forEach((f) => formData.append("files", f));
          }

          // We await this so it's registered in the backend before we load the new page
          await apiFetch("/chat/message", {
            method: "POST",
            body: formData,
          });

          router.push(`/assessments/${data.sessionId}`);
        }
      } catch (error) {
        console.error("Failed to start assessment from landing:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [message, files, router],
  );

  return (
    <section aria-labelledby="composer-heading">
      <h2
        id="composer-heading"
        className="text-sm font-semibold text-[var(--text-primary)]"
      >
        Begin your assessment
      </h2>
      {/*<p className="mt-1 text-xs text-[var(--text-tertiary)]">*/}
      {/*  Everything here is client-side only—starting a session uses a demo route for stakeholder*/}
      {/*  walkthroughs.*/}
      {/*</p>*/}

      <form
        onSubmit={onSubmit}
        className="mt-5 overflow-hidden rounded-2xl border border-[var(--border-strong)] bg-[var(--bg-elevated)] shadow-[var(--shadow-card)] ring-1 ring-[color-mix(in_srgb,var(--brand-primary)_12%,transparent)]"
      >
        <div className="border-b border-[var(--border-subtle)] bg-[color-mix(in_srgb,var(--bg-muted)_85%,transparent)] px-4 py-3">
          <p className="text-[11px] font-medium uppercase tracking-wider text-[var(--text-tertiary)]">
            Assessment brief
          </p>
        </div>

        <label htmlFor="landing-assessment-input" className="sr-only">
          Assessment brief
        </label>
        <textarea
          id="landing-assessment-input"
          name="brief"
          rows={2}
          value={message}
          onChange={(e) => onMessageChange?.(e.target.value)}
          placeholder={PLACEHOLDER}
          className="w-full resize-y border-0 bg-transparent px-4 py-4 text-sm leading-relaxed text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-0"
        />

        {files.length > 0 ? (
          <ul className="border-t border-[var(--border-subtle)] px-4 py-2 text-xs text-[var(--text-secondary)]">
            {files.map((f) => (
              <li key={f.name + f.size} className="truncate">
                {f.name}
              </li>
            ))}
          </ul>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-subtle)] bg-[var(--bg-muted)] px-3 py-3">
          <div className="flex flex-wrap items-center gap-2">
            {/*<input*/}
            {/*  ref={fileInputRef}*/}
            {/*  type="file"*/}
            {/*  className="sr-only"*/}
            {/*  accept=".csv,.pdf,.json,.yaml,.yml,.txt,.md,.png,.jpg,.jpeg"*/}
            {/*  multiple*/}
            {/*  onChange={onFilesPicked}*/}
            {/*/>*/}
            {/*<button*/}
            {/*  type="button"*/}
            {/*  onClick={openFilePicker}*/}
            {/*  className="focus-ring inline-flex items-center gap-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-app)] px-3 py-2 text-xs font-medium text-[var(--text-secondary)] transition hover:border-[color-mix(in_srgb,var(--brand-primary)_35%,var(--border-subtle))] hover:text-[var(--text-primary)]"*/}
            {/*>*/}
            {/*  <svg*/}
            {/*    className="h-4 w-4 shrink-0 text-[var(--brand-primary)]"*/}
            {/*    fill="none"*/}
            {/*    viewBox="0 0 24 24"*/}
            {/*    stroke="currentColor"*/}
            {/*    aria-hidden*/}
            {/*  >*/}
            {/*    <path*/}
            {/*      strokeLinecap="round"*/}
            {/*      strokeLinejoin="round"*/}
            {/*      strokeWidth={2}*/}
            {/*      d="M18.375 12.739l-7.27 7.27a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.872l-10.94 10.94a1.5 1.5 0 002.122 2.122l7.27-7.27"*/}
            {/*    />*/}
            {/*  </svg>*/}
            {/*  Attach files*/}
            {/*</button>*/}
            {/*<span className="hidden text-[11px] text-[var(--text-tertiary)] sm:inline">*/}
            {/*  Cost exports, diagrams, architecture notes*/}
            {/*</span>*/}
          </div>

          <button
            type="submit"
            disabled={isSubmitting || (!message.trim() && files.length === 0)}
            className="focus-ring inline-flex min-w-[7rem] items-center justify-center gap-2 rounded-full bg-brand-gradient px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Starting..." : "Start assessment"}
            {!isSubmitting && <span aria-hidden>→</span>}
          </button>
        </div>
      </form>
    </section>
  );
}
